import React, { useState, useRef, useCallback, useEffect } from "react";
import "./imageCropModal.css";

const HANDLE_SIZE = 12; // px, visual + hit-area size of each drag handle
const MIN_BOX = 20; // minimum crop width/height in displayed px

const ImageCropModal = ({
    open,
    imageSrc,
    targetWidth = 1800,
    targetHeight = 900,
    freeAspect = true,
    onCancel,
    onComplete,
}) => {
    const imgRef = useRef(null);
    const containerRef = useRef(null);
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
    const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

    // Crop box in DISPLAYED (rendered) pixel coordinates, relative to the
    // top-left of the image element.
    const [box, setBox] = useState({ x: 0, y: 0, w: 0, h: 0 });

    const [processing, setProcessing] = useState(false);

    // Drag state lives in a ref so mousemove handlers always see latest
    // values without re-binding listeners on every render.
    const dragRef = useRef(null); // { mode: 'move'|'n'|'s'|'e'|'w'|'ne'|'nw'|'se'|'sw', startX, startY, startBox }

    const initBoxForImage = useCallback(
        (w, h) => {
            if (freeAspect) {
                // Full-image box to start
                setBox({ x: 0, y: 0, w, h });
            } else {
                const aspect = targetWidth / targetHeight;
                let boxW = w;
                let boxH = boxW / aspect;
                if (boxH > h) {
                    boxH = h;
                    boxW = boxH * aspect;
                }
                setBox({
                    x: (w - boxW) / 2,
                    y: (h - boxH) / 2,
                    w: boxW,
                    h: boxH,
                });
            }
        },
        [freeAspect, targetWidth, targetHeight]
    );

    const onImageLoad = useCallback(
        (e) => {
            const img = e.currentTarget;
            const w = img.clientWidth;
            const h = img.clientHeight;
            setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
            setDisplaySize({ w, h });
            initBoxForImage(w, h);
        },
        [initBoxForImage]
    );

    // Re-clamp box if the window resizes and the rendered image size changes
    useEffect(() => {
        if (!open) return;
        const handleResize = () => {
            const img = imgRef.current;
            if (!img) return;
            const w = img.clientWidth;
            const h = img.clientHeight;
            if (w !== displaySize.w || h !== displaySize.h) {
                setDisplaySize({ w, h });
                initBoxForImage(w, h);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, displaySize.w, displaySize.h]);

    const clampBox = useCallback(
        (next) => {
            const maxW = displaySize.w;
            const maxH = displaySize.h;
            let { x, y, w, h } = next;

            w = Math.max(MIN_BOX, w);
            h = Math.max(MIN_BOX, h);

            if (x < 0) x = 0;
            if (y < 0) y = 0;
            if (x + w > maxW) {
                if (w > maxW) w = maxW;
                x = maxW - w;
            }
            if (y + h > maxH) {
                if (h > maxH) h = maxH;
                y = maxH - h;
            }
            return { x, y, w, h };
        },
        [displaySize]
    );

    const startDrag = (mode) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        const point = e.touches ? e.touches[0] : e;
        dragRef.current = {
            mode,
            startX: point.clientX,
            startY: point.clientY,
            startBox: { ...box },
        };
        window.addEventListener("mousemove", handleDragMove);
        window.addEventListener("mouseup", handleDragEnd);
        window.addEventListener("touchmove", handleDragMove, { passive: false });
        window.addEventListener("touchend", handleDragEnd);
    };

    const handleDragMove = useCallback(
        (e) => {
            const drag = dragRef.current;
            if (!drag) return;
            if (e.cancelable) e.preventDefault();

            const point = e.touches ? e.touches[0] : e;
            const dx = point.clientX - drag.startX;
            const dy = point.clientY - drag.startY;
            const { mode, startBox } = drag;

            let next = { ...startBox };
            const aspect = !freeAspect ? targetWidth / targetHeight : null;

            switch (mode) {
                case "move":
                    next.x = startBox.x + dx;
                    next.y = startBox.y + dy;
                    break;
                case "e":
                    next.w = startBox.w + dx;
                    if (aspect) next.h = next.w / aspect;
                    break;
                case "w":
                    next.x = startBox.x + dx;
                    next.w = startBox.w - dx;
                    if (aspect) {
                        next.h = next.w / aspect;
                    }
                    break;
                case "s":
                    next.h = startBox.h + dy;
                    if (aspect) next.w = next.h * aspect;
                    break;
                case "n":
                    next.y = startBox.y + dy;
                    next.h = startBox.h - dy;
                    if (aspect) next.w = next.h * aspect;
                    break;
                case "se":
                    next.w = startBox.w + dx;
                    next.h = aspect ? next.w / aspect : startBox.h + dy;
                    break;
                case "sw":
                    next.x = startBox.x + dx;
                    next.w = startBox.w - dx;
                    next.h = aspect ? next.w / aspect : startBox.h + dy;
                    break;
                case "ne":
                    next.w = startBox.w + dx;
                    next.h = aspect ? next.w / aspect : startBox.h - dy;
                    if (!aspect) next.y = startBox.y + dy;
                    else next.y = startBox.y + (startBox.h - next.h);
                    break;
                case "nw":
                    next.x = startBox.x + dx;
                    next.w = startBox.w - dx;
                    next.h = aspect ? next.w / aspect : startBox.h - dy;
                    if (!aspect) next.y = startBox.y + dy;
                    else next.y = startBox.y + (startBox.h - next.h);
                    break;
                default:
                    break;
            }

            setBox(clampBox(next));
        },
        [freeAspect, targetWidth, targetHeight, clampBox]
    );

    const handleDragEnd = useCallback(() => {
        dragRef.current = null;
        window.removeEventListener("mousemove", handleDragMove);
        window.removeEventListener("mouseup", handleDragEnd);
        window.removeEventListener("touchmove", handleDragMove);
        window.removeEventListener("touchend", handleDragEnd);
    }, [handleDragMove]);

    useEffect(() => {
        return () => {
            // Cleanup listeners if the modal unmounts mid-drag
            window.removeEventListener("mousemove", handleDragMove);
            window.removeEventListener("mouseup", handleDragEnd);
            window.removeEventListener("touchmove", handleDragMove);
            window.removeEventListener("touchend", handleDragEnd);
        };
    }, [handleDragMove, handleDragEnd]);

    const getCroppedBlob = () =>
        new Promise((resolve, reject) => {
            const image = imgRef.current;
            if (!image || !displaySize.w || !displaySize.h) {
                reject(new Error("Image not ready"));
                return;
            }

            const scaleX = naturalSize.w / displaySize.w;
            const scaleY = naturalSize.h / displaySize.h;

            const cropX = box.x * scaleX;
            const cropY = box.y * scaleY;
            const cropW = box.w * scaleX;
            const cropH = box.h * scaleY;

            if (!cropW || !cropH) {
                reject(new Error("No crop selected"));
                return;
            }

            // Output canvas matches the ACTUAL cropped region's pixel size
            // — whatever ratio was dragged — so the ratio stays dynamic
            // instead of being forced into a fixed target size.
            let outW = Math.round(cropW);
            let outH = Math.round(cropH);

            // Optional safety cap so a huge source photo can't produce an
            // enormous output file. Scales down proportionally.
            const MAX_DIMENSION = 2500;
            if (outW > MAX_DIMENSION || outH > MAX_DIMENSION) {
                const scale = MAX_DIMENSION / Math.max(outW, outH);
                outW = Math.round(outW * scale);
                outH = Math.round(outH * scale);
            }

            const canvas = document.createElement("canvas");
            canvas.width = outW;
            canvas.height = outH;
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingQuality = "high";

            ctx.drawImage(
                image,
                cropX,
                cropY,
                cropW,
                cropH,
                0,
                0,
                outW,
                outH
            );

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error("Canvas is empty"));
                        return;
                    }
                    resolve(blob);
                },
                "image/jpeg",
                0.92
            );
        });

    const handleOk = async () => {
        try {
            setProcessing(true);
            const blob = await getCroppedBlob();
            const previewUrl = URL.createObjectURL(blob);
            onComplete(blob, previewUrl);
        } catch (err) {
            console.error("Crop failed:", err);
        } finally {
            setProcessing(false);
        }
    };

    if (!open) return null;

    const handles = [
        { key: "nw", cursor: "nwse-resize", style: { left: -HANDLE_SIZE / 2, top: -HANDLE_SIZE / 2 } },
        { key: "n", cursor: "ns-resize", style: { left: "50%", top: -HANDLE_SIZE / 2, marginLeft: -HANDLE_SIZE / 2 } },
        { key: "ne", cursor: "nesw-resize", style: { right: -HANDLE_SIZE / 2, top: -HANDLE_SIZE / 2 } },
        { key: "e", cursor: "ew-resize", style: { right: -HANDLE_SIZE / 2, top: "50%", marginTop: -HANDLE_SIZE / 2 } },
        { key: "se", cursor: "nwse-resize", style: { right: -HANDLE_SIZE / 2, bottom: -HANDLE_SIZE / 2 } },
        { key: "s", cursor: "ns-resize", style: { left: "50%", bottom: -HANDLE_SIZE / 2, marginLeft: -HANDLE_SIZE / 2 } },
        { key: "sw", cursor: "nesw-resize", style: { left: -HANDLE_SIZE / 2, bottom: -HANDLE_SIZE / 2 } },
        { key: "w", cursor: "ew-resize", style: { left: -HANDLE_SIZE / 2, top: "50%", marginTop: -HANDLE_SIZE / 2 } },
    ];

    return (
        <div className="icm-overlay">
            <div className="icm-modal">
                <div className="icm-header">
                    <h3>Edit Image</h3>
                    <button className="icm-close" onClick={onCancel} aria-label="Close">
                        ✕
                    </button>
                </div>

                <div className="icm-body">
                    <div
                        ref={containerRef}
                        className="icm-crop-container"
                        style={{
                            position: "relative",
                            display: "inline-block",
                            userSelect: "none",
                        }}
                    >
                        <img
                            ref={imgRef}
                            src={imageSrc}
                            alt="Crop source"
                            onLoad={onImageLoad}
                            className="icm-image"
                            draggable={false}
                        />

                        {/* Dimmed overlay outside the crop box */}
                        {displaySize.w > 0 && (
                            <svg
                                width={displaySize.w}
                                height={displaySize.h}
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    pointerEvents: "none",
                                }}
                            >
                                <defs>
                                    <mask id="icm-crop-mask">
                                        <rect width={displaySize.w} height={displaySize.h} fill="white" />
                                        <rect x={box.x} y={box.y} width={box.w} height={box.h} fill="black" />
                                    </mask>
                                </defs>
                                <rect
                                    width={displaySize.w}
                                    height={displaySize.h}
                                    fill="rgba(0,0,0,0.5)"
                                    mask="url(#icm-crop-mask)"
                                />
                            </svg>
                        )}

                        {/* Crop box */}
                        {displaySize.w > 0 && (
                            <div
                                className="icm-crop-box"
                                onMouseDown={startDrag("move")}
                                onTouchStart={startDrag("move")}
                                style={{
                                    position: "absolute",
                                    left: box.x,
                                    top: box.y,
                                    width: box.w,
                                    height: box.h,
                                    border: "1px solid #2563eb",
                                    boxSizing: "border-box",
                                    cursor: "move",
                                }}
                            >
                                {/* Rule-of-thirds guide lines */}
                                <div className="icm-grid-line icm-grid-v1" />
                                <div className="icm-grid-line icm-grid-v2" />
                                <div className="icm-grid-line icm-grid-h1" />
                                <div className="icm-grid-line icm-grid-h2" />

                                {handles.map(({ key, cursor, style }) => (
                                    <div
                                        key={key}
                                        onMouseDown={startDrag(key)}
                                        onTouchStart={startDrag(key)}
                                        style={{
                                            position: "absolute",
                                            width: HANDLE_SIZE,
                                            height: HANDLE_SIZE,
                                            background: "#2563eb",
                                            border: "1px solid #fff",
                                            borderRadius: 2,
                                            cursor,
                                            zIndex: 2,
                                            ...style,
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="icm-hint">
                    {freeAspect
                        ? "Drag any edge or corner to crop freely. Output keeps your exact crop size/ratio."
                        : `Drag any handle - the box keeps a ${targetWidth}:${targetHeight} ratio.`}
                </div>

                <div className="icm-footer">
                    <button className="icm-btn icm-btn--cancel" onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        className="icm-btn icm-btn--ok"
                        onClick={handleOk}
                        disabled={processing}
                    >
                        {processing ? "Processing…" : "OK"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;